import React, { useEffect, useId, useMemo, useRef, useState } from 'react'
import { FaChevronDown, FaSearch, FaTimes, FaPlus } from 'react-icons/fa'

/**
 * A dropdown you can type into.
 *
 * A plain <select> is the wrong control for a list that grows: it cannot be
 * searched, it cannot show a second line of context, and picking the right
 * "Lorenzini" out of ninety-one products by scrolling is slower than typing
 * three letters. This is what makes the invoice editor quick — every field that
 * can be filled from something already in the database is filled by choosing it
 * here rather than retyping it.
 *
 * What it deliberately does:
 *
 *   · filters on every word, in any order, across a searchable blob per option,
 *     so "navy carter" finds "Carter Pique Golfer — Navy";
 *   · groups options under headings, so suppliers and customers can share one
 *     list without being confused for each other;
 *   · offers to use the typed text as-is when `allowFreeText` is set, because a
 *     one-off charge that is in no list must not be a dead end;
 *   · is driven entirely from the keyboard — arrows, Enter, Escape — since this
 *     is a form somebody fills in twenty lines at a time.
 */
const Combobox = ({
  options = [],
  value = null,
  onSelect,
  onFreeText,
  allowFreeText = false,
  placeholder = 'Search…',
  emptyLabel = 'Nothing matches',
  freeTextLabel = (text) => `Use “${text}”`,
  disabled = false,
  size = 'md',
  ariaLabel,
  renderTrigger,
}) => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const rootRef = useRef(null)
  const inputRef = useRef(null)
  const listRef = useRef(null)
  const listId = useId()

  // Close on an outside click or Escape. A dropdown that stays open behind the
  // next thing you click is worse than one that closes too eagerly.
  useEffect(() => {
    if (!open) return
    const onPointerDown = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  useEffect(() => {
    if (open) inputRef.current?.focus()
    else setQuery('')
  }, [open])

  const matches = useMemo(() => {
    const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
    if (words.length === 0) return options
    return options.filter((option) => {
      const haystack = (option.search ?? `${option.label} ${option.hint ?? ''}`).toLowerCase()
      return words.every((word) => haystack.includes(word))
    })
  }, [options, query])

  // Reset the highlight whenever the list changes under it, or Enter picks
  // whatever happens to be at the old index.
  useEffect(() => setActive(0), [query, options])

  const showFreeText = allowFreeText && query.trim().length > 0
  const rows = showFreeText ? [...matches, { __freeText: true }] : matches

  const commit = (row) => {
    if (!row) return
    if (row.__freeText) onFreeText?.(query.trim())
    else onSelect?.(row)
    setOpen(false)
  }

  const onKeyDown = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      setActive((current) => {
        const next = event.key === 'ArrowDown' ? current + 1 : current - 1
        return Math.max(0, Math.min(rows.length - 1, next))
      })
    } else if (event.key === 'Enter') {
      event.preventDefault()
      commit(rows[active])
    } else if (event.key === 'Escape') {
      event.preventDefault()
      setOpen(false)
    }
  }

  // Keep the highlighted row in view when arrowing past the fold.
  useEffect(() => {
    if (!open) return
    listRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ block: 'nearest' })
  }, [active, open])

  let lastGroup = null

  return (
    <div className="relative" ref={rootRef}>
      {renderTrigger ? (
        renderTrigger({ open: () => !disabled && setOpen((o) => !o), disabled })
      ) : (
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={ariaLabel}
          className={`input flex items-center justify-between gap-2 text-left disabled:cursor-not-allowed disabled:opacity-60 ${
            size === 'sm' ? 'input-sm' : ''
          }`}
        >
          <span className={`min-w-0 flex-1 truncate ${value ? 'text-ink' : 'text-ink-muted/80'}`}>
            {value ?? placeholder}
          </span>
          <FaChevronDown className="shrink-0 text-[9px] text-ink-muted" aria-hidden="true" />
        </button>
      )}

      {open && (
        <div className="absolute left-0 right-0 z-40 mt-1 min-w-[260px] overflow-hidden rounded-lg border border-hairline bg-surface shadow-pop">
          <div className="relative border-b border-hairline">
            <FaSearch
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[10px] text-ink-muted"
              aria-hidden="true"
            />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={placeholder}
              aria-label={ariaLabel ?? placeholder}
              aria-controls={listId}
              className="w-full border-0 bg-transparent py-2 pl-8 pr-3 text-[13px] text-ink placeholder:text-ink-muted/70 focus:outline-none"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-ink-muted hover:text-ink"
                aria-label="Clear the search"
              >
                <FaTimes className="text-[9px]" />
              </button>
            )}
          </div>

          <div ref={listRef} id={listId} role="listbox" className="max-h-72 overflow-y-auto py-1">
            {rows.length === 0 && (
              <p className="px-3 py-4 text-center text-2xs text-ink-muted">{emptyLabel}</p>
            )}

            {rows.map((row, index) => {
              if (row.__freeText) {
                return (
                  <button
                    key="__freeText"
                    type="button"
                    role="option"
                    aria-selected={index === active}
                    data-active={index === active}
                    onMouseEnter={() => setActive(index)}
                    onClick={() => commit(row)}
                    className={`flex w-full items-center gap-2 border-t border-hairline px-3 py-2 text-left text-2xs ${
                      index === active ? 'bg-plane text-ink' : 'text-ink-secondary'
                    }`}
                  >
                    <FaPlus className="shrink-0 text-[9px] text-ink-muted" aria-hidden="true" />
                    <span className="truncate">{freeTextLabel(query.trim())}</span>
                  </button>
                )
              }

              const heading = row.group && row.group !== lastGroup ? row.group : null
              lastGroup = row.group ?? lastGroup

              return (
                <React.Fragment key={row.id ?? row.label}>
                  {heading && (
                    <div className="px-3 pb-1 pt-2 text-2xs font-semibold uppercase tracking-[0.08em] text-ink-muted">
                      {heading}
                    </div>
                  )}
                  <button
                    type="button"
                    role="option"
                    aria-selected={index === active}
                    data-active={index === active}
                    onMouseEnter={() => setActive(index)}
                    onClick={() => commit(row)}
                    className={`flex w-full items-start gap-2 px-3 py-1.5 text-left ${
                      index === active ? 'bg-plane' : ''
                    }`}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[13px] text-ink">{row.label}</span>
                      {row.hint && (
                        <span className="block truncate text-2xs text-ink-muted">{row.hint}</span>
                      )}
                    </span>
                    {row.trailing && (
                      <span className="shrink-0 whitespace-nowrap pt-0.5 text-2xs tabular-nums text-ink-secondary">
                        {row.trailing}
                      </span>
                    )}
                  </button>
                </React.Fragment>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

export default Combobox
